import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import { logger } from '@/lib/logger'

const schema = z
  .object({
    email: z.string().email(),
    password: z.string().min(8),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'auth.error.passwordsMismatch',
  })

type FormData = z.infer<typeof schema>

export function RegisterForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
  })

  const onSubmit = async (data: FormData) => {
    setServerError(null)
    const { error } = await supabase.auth.signUp({ email: data.email, password: data.password })
    if (error) {
      logger.warn('Registration error', { userId: undefined })
      setServerError(t('auth.error.registrationFailed'))
      return
    }
    navigate({ to: '/household-setup' })
  }

  return (
    <div className="w-full max-w-sm">
      <div className="text-center mb-8">
        <span className="text-3xl font-bold tracking-tight text-accent">mise</span>
        <p className="mt-1 text-sm text-muted">Plan your week. Shop with confidence.</p>
      </div>

      <div className="bg-surface rounded-2xl border border-line p-8 shadow-sm">
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
          <h1 className="text-lg font-semibold text-ink">{t('auth.register.title')}</h1>

          <div className="space-y-1">
            <label htmlFor="email" className="block text-sm font-medium text-muted">
              {t('auth.register.emailLabel')}
            </label>
            <input
              id="email"
              type="email"
              className="w-full rounded-lg border border-line bg-canvas px-3.5 py-2.5 text-ink placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition"
              {...register('email')}
            />
            {errors.email && (
              <span role="alert" className="block text-xs text-danger mt-1">
                {t('auth.error.emailInvalid')}
              </span>
            )}
          </div>

          <div className="space-y-1">
            <label htmlFor="password" className="block text-sm font-medium text-muted">
              {t('auth.register.passwordLabel')}
            </label>
            <input
              id="password"
              type="password"
              className="w-full rounded-lg border border-line bg-canvas px-3.5 py-2.5 text-ink placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition"
              {...register('password')}
            />
            {errors.password && (
              <span role="alert" className="block text-xs text-danger mt-1">
                {t('auth.error.passwordTooShort')}
              </span>
            )}
          </div>

          <div className="space-y-1">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-muted">
              {t('auth.register.confirmPasswordLabel')}
            </label>
            <input
              id="confirmPassword"
              type="password"
              className="w-full rounded-lg border border-line bg-canvas px-3.5 py-2.5 text-ink placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition"
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <span role="alert" className="block text-xs text-danger mt-1">
                {t('auth.error.passwordsMismatch')}
              </span>
            )}
          </div>

          {serverError && (
            <div role="alert" className="rounded-lg bg-danger/10 border border-danger/20 px-3.5 py-2.5 text-sm text-danger">
              {serverError}
            </div>
          )}

          <button
            type="submit"
            className="w-full rounded-lg bg-accent text-white font-semibold py-2.5 px-4 hover:bg-accent-dark active:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-accent transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('auth.register.submitButton')}
          </button>

          <p className="text-center text-sm text-muted">
            <Link to="/login" className="text-accent hover:text-accent-dark font-medium transition">
              {t('auth.login.linkToRegister')}
            </Link>
          </p>
        </form>
      </div>
    </div>
  )
}
